import { gql } from '@apollo/client';
import { Box, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from 'i18next';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { CellProps, Column, TableInstance, useSortBy, useTable } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { useAnalyticsReporter } from '~utils/analytics';
import { compareMaybeStrings } from '~utils/compare';
import { useFeatureFlag } from '~utils/features';
import { MaybePromise } from '~utils/types';
import { PlaylistTable_PlaylistFragment } from './__generated__/PlaylistTable.graphql';
import { ActionsCell_PlaylistFragment } from './Cell/__generated__/ActionsCell.graphql';
import { ActionsCell } from './Cell/ActionsCell';
import { CreatedAtCell } from './Cell/CreateAtCell';
import { DescriptionCell } from './Cell/DescriptionCell';
import { PlaylistTypeCell, PlaylistTypeHeader } from './Cell/PlaylistTypeCell';
import { SizeCell } from './Cell/SizeCell';
import { TitleCell } from './Cell/TitleCell';
import { UsedByCell } from './Cell/UsedByCell';

export enum Columns {
  Title = 'title',
  Description = 'description',
  Size = 'size',
  CreatedAt = 'createdAt',
  UsedBy = 'usedBy',
  Actions = 'actions',
  PlaylistType = 'playlistType',
  Duplicate = 'duplicate',
}

export function usePlaylistTable(
  data: PlaylistTable_PlaylistFragment[],
  canCreatePlaylist: boolean,
) {
  const { isEnabled: isPlaylistTypesEnabled } = useFeatureFlag('playlistTypes');
  const columnsWithPlaylistTypes = useMemo<Array<Column<PlaylistTable_PlaylistFragment>>>(
    () => [
      {
        id: Columns.Title,
        Header: t('title'),
        accessor: (p) => p.title,
        Cell: TitleCell,
        width: '200px',
        minWidth: 220,
        maxWidth: 220,
      },
      {
        id: Columns.Size,
        Header: t('size'),
        accessor: (p) => p.size,
        Cell: SizeCell,
        width: '112px',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        id: Columns.Description,
        Header: t('description'),
        accessor: (p) => p.description,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(rowA.original.description, rowB.original.description),
        Cell: DescriptionCell,
        width: '250px',
        minWidth: 220,
        maxWidth: 220,
      },

      {
        id: Columns.CreatedAt,
        Header: t('dateAdded'),
        accessor: (d) => d.createdAt,
        Cell: CreatedAtCell,
        width: '210px',
        minWidth: 100,
        maxWidth: 100,
      },
      {
        id: Columns.UsedBy,
        Header: t('Displays'),
        accessor: (d) => d,
        Cell: UsedByCell,
        width: '110px',
        minWidth: 210,
        maxWidth: 210,
      },
      {
        id: Columns.PlaylistType,
        Header: PlaylistTypeHeader,
        accessor: (p) => p.playlistType,
        Cell: PlaylistTypeCell,
        width: '200px',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        id: Columns.Actions,
        Header: '',
        accessor: (d) => d,
        Cell: (
          cellProps: PropsWithChildren<
            CellProps<PlaylistTable_PlaylistFragment, ActionsCell_PlaylistFragment>
          >,
        ) => <ActionsCell {...cellProps} canCreatePlaylist={canCreatePlaylist} />,
        width: '70px',
        minWidth: 60,
        maxWidth: 60,
      },
    ],
    [canCreatePlaylist],
  );

  const columnsWithoutPlaylistTypes = useMemo<Array<Column<PlaylistTable_PlaylistFragment>>>(
    () => [
      {
        id: Columns.Title,
        Header: t('title'),
        accessor: (p) => p.title,
        Cell: TitleCell,
        width: '230px',
        minWidth: 230,
        maxWidth: 230,
      },
      {
        id: Columns.Description,
        Header: t('description'),
        accessor: (p) => p.description,
        sortType: (rowA, rowB) =>
          compareMaybeStrings(rowA.original.description, rowB.original.description),
        Cell: DescriptionCell,
        width: '350px',
        minWidth: 350,
        maxWidth: 350,
      },
      {
        id: Columns.Size,
        Header: t('size'),
        accessor: (p) => p.size,
        Cell: SizeCell,
        width: '120px',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        id: Columns.CreatedAt,
        Header: t('dateAdded'),
        accessor: (d) => d.createdAt,
        Cell: CreatedAtCell,
        width: 'auto',
      },
      {
        id: Columns.UsedBy,
        Header: t('Displays'),
        accessor: (d) => d,
        Cell: UsedByCell,
        width: '250px',
        minWidth: 250,
        maxWidth: 250,
      },
      {
        id: Columns.Actions,
        Header: '',
        accessor: (d) => d,
        Cell: ActionsCell,
        width: '70px',
        minWidth: 70,
        maxWidth: 70,
      },
    ],
    [],
  );
  const table = useTable(
    {
      columns: isPlaylistTypesEnabled ? columnsWithPlaylistTypes : columnsWithoutPlaylistTypes,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
    },
    useSortBy,
  );

  return table;
}

/* eslint-disable react/jsx-key */
export function PlaylistTable({
  table,
  onGoToDetail,
}: {
  table: TableInstance<PlaylistTable_PlaylistFragment>;
  onGoToDetail: (id: string) => MaybePromise<void>;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = table;
  const analytics = useAnalyticsReporter();

  return (
    <Table {...getTableProps()} variant="simple" width="100%">
      <Thead>
        {headerGroups.map((headerGroup) => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              const headerProps = column.getHeaderProps(column.getSortByToggleProps());
              return (
                <Th
                  {...headerProps}
                  color={column.isSorted ? 'gray.700' : 'gray.600'}
                  minWidth={`${column.minWidth}px`}
                  maxWidth={`${column.maxWidth}px`}
                  width={column.width}
                  _hover={
                    column.disableSortBy
                      ? {}
                      : {
                          textColor: 'gray.800',
                        }
                  }
                  onClick={(e) => {
                    if (column.id !== 'selection') {
                      analytics.track('playlistTableSort', { sortType: column.id });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (headerProps as any).onClick?.(e);
                  }}
                >
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <Box flex="1">{column.render('Header') as ReactNode}</Box>
                    {column.canSort && (
                      <Box>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ChevronDownIcon display="inherit" />
                          ) : (
                            <ChevronUpIcon display="inherit" />
                          )
                        ) : (
                          <ChevronDownIcon visibility="hidden" />
                        )}
                      </Box>
                    )}
                  </Box>
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);

          return (
            <Tr
              {...row.getRowProps()}
              role="button"
              _hover={{
                background: 'blue.50',
                cursor: 'pointer',
              }}
            >
              {row.cells.map((cell) => {
                return (
                  <Td
                    minWidth={`${cell.column.minWidth}px`}
                    maxWidth={`${cell.column.maxWidth}px`}
                    width={cell.column.width}
                    onClick={() => {
                      analytics.track('playlistTableItemClick');
                      onGoToDetail(row.original.id);
                    }}
                    {...cell.getCellProps()}
                  >
                    {cell.render('Cell') as ReactNode}
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
/* eslint-enable react/jsx-key */

PlaylistTable.graphql = {
  fragments: {
    PlaylistTable_playlist: gql`
      fragment PlaylistTable_playlist on Playlist {
        id
        ...TitleCell_playlist
        ...DescriptionCell_playlist
        ...SizeCell_playlist
        ...PlaylistTypeCell_playlist
        ...CreatedAtCell_playlist
        ...UsedByCell_playlist
        ...ActionsCell_playlist
      }
    `,
  },
};
